import { lazy } from "react";
import type { WidgetDefinition } from "@pulse-ui/widget-sdk";

export const CLOUD_WIDGETS: Record<string, WidgetDefinition> = {
  "positions-list-widget": {
    id: "positions-list-widget",
    name: "Positions (List)",
    description: "Live position quantities and unrealised P&L",
    category: "Trading",
    component: lazy(() => import("@pulse-ui/positions-list-widget")),
  },
  "positions-notional-widget": {
    id: "positions-notional-widget",
    name: "Positions (Notional)",
    description: "Live positions with notional values in a selected quote asset",
    category: "Trading",
    component: lazy(() => import("@pulse-ui/positions-notional-widget")),
  },
  "open-orders-widget": {
    id: "open-orders-widget",
    name: "Open Orders",
    description: "Live open orders with real-time execution updates",
    category: "Trading",
    component: lazy(() => import("@pulse-ui/open-orders-widget")),
  },
  "position-summary-widget": {
    id: "position-summary-widget",
    name: "Position Summary",
    description: "Aggregated position summary with notional values per portfolio",
    category: "Trading",
    component: lazy(() => import("@pulse-ui/position-summary-widget")),
  },
  "balances-list-widget": {
    id: "balances-list-widget",
    name: "Balances (List)",
    description: "Live account balances with raw quantities",
    category: "Trading",
    component: lazy(() => import("@pulse-ui/balances-list-widget")),
  },
  "balances-notional-widget": {
    id: "balances-notional-widget",
    name: "Balances (Notional)",
    description: "Live account balances converted to a selected quote asset",
    category: "Trading",
    component: lazy(() => import("@pulse-ui/balances-notional-widget")),
  },
  "blotter-orders-widget": {
    id: "blotter-orders-widget",
    name: "Blotter: Orders",
    description: "Historical order events with date filtering and pagination",
    category: "Blotter",
    component: lazy(() => import("@pulse-ui/blotter-orders-widget")),
  },
  "blotter-trades-widget": {
    id: "blotter-trades-widget",
    name: "Blotter: Trades",
    description: "Historical trade events with date filtering and pagination",
    category: "Blotter",
    component: lazy(() => import("@pulse-ui/blotter-trades-widget")),
  },
  "blotter-transfers-widget": {
    id: "blotter-transfers-widget",
    name: "Blotter: Transfers",
    description: "Historical transfer events with date filtering and pagination",
    category: "Blotter",
    component: lazy(() => import("@pulse-ui/blotter-transfers-widget")),
  },
  "blotter-positions-widget": {
    id: "blotter-positions-widget",
    name: "Blotter: Positions",
    description: "Historical position updates with expandable pos_qtys detail",
    category: "Blotter",
    component: lazy(() => import("@pulse-ui/blotter-positions-widget")),
  },
  "blotter-balances-widget": {
    id: "blotter-balances-widget",
    name: "Blotter: Balances",
    description: "Historical balance updates with expandable amounts detail",
    category: "Blotter",
    component: lazy(() => import("@pulse-ui/blotter-balances-widget")),
  },
  "blotter-asset-margin-widget": {
    id: "blotter-asset-margin-widget",
    name: "Blotter: Asset Margin",
    description: "Historical asset margin reports with date filtering and pagination",
    category: "Blotter",
    component: lazy(() => import("@pulse-ui/blotter-asset-margin-widget")),
  },
  "blotter-position-margin-widget": {
    id: "blotter-position-margin-widget",
    name: "Blotter: Position Margin",
    description: "Historical position margin reports with date filtering and pagination",
    category: "Blotter",
    component: lazy(() => import("@pulse-ui/blotter-position-margin-widget")),
  },
  "overview-widget": {
    id: "overview-widget",
    name: "Overview",
    description: "Account equity, GMV, leverage, P&L and volume per portfolio",
    category: "Trading",
    component: lazy(() => import("@pulse-ui/overview-widget")),
  },
  "order-book-widget": {
    id: "order-book-widget",
    name: "Order Book",
    description: "Live order book with open-order overlay for a selected symbol",
    category: "Trading",
    component: lazy(() => import("@pulse-ui/order-book-widget")),
  },
  "algo-table-widget": {
    id: "algo-table-widget",
    name: "Algo Table",
    description: "Live strategy instance table with right-click RPC interactions",
    category: "Algo",
    component: lazy(() => import("@pulse-ui/algo-table-widget")),
    adminOnly: true,
  },
  "alert-manager-widget": {
    id: "alert-manager-widget",
    name: "Alert Manager",
    description: "Manage, start, edit and monitor alerts across all portfolios and accounts",
    category: "Alerts",
    component: lazy(() => import("@pulse-ui/alert-manager-widget")),
    adminOnly: true,
  },
  "strategy-management-widget": {
    id: "strategy-management-widget",
    name: "Strategy Management",
    description: "Create and delete strategies",
    category: "Management",
    component: lazy(() => import("@pulse-ui/strategy-management-widget")),
    adminOnly: true,
    isLinkable: false,
  },
  "role-management-widget": {
    id: "role-management-widget",
    name: "Role Management",
    description: "Manage organization users, roles, and portfolio access",
    category: "Management",
    component: lazy(() => import("@pulse-ui/role-management-widget")),
    adminOnly: true,
    isLinkable: false,
  },
  "entity-management-widget": {
    id: "entity-management-widget",
    name: "Entity Management",
    description: "Manage portfolios, accounts, and API keys",
    category: "Management",
    component: lazy(() => import("@pulse-ui/entity-management-widget")),
    adminOnly: true,
    isLinkable: false,
  },
  "manual-adjustments-widget": {
    id: "manual-adjustments-widget",
    name: "Manual Adjustments",
    description: "Upload and manage manual balance and position adjustments per portfolio",
    category: "Management",
    component: lazy(() => import("@pulse-ui/manual-adjustments-widget")),
    adminOnly: true,
    isLinkable: false,
  },
  "static-price-overrides-widget": {
    id: "static-price-overrides-widget",
    name: "Static Price Overrides",
    description: "Manage static price overrides per symbol with a weekly schedule calendar",
    category: "Management",
    component: lazy(() => import("@pulse-ui/static-price-overrides-widget")),
    isLinkable: false,
  },
};

export function getCloudWidgetRegistry(
  isAdmin: boolean,
  userRoles: string[] = []
): Record<string, WidgetDefinition> {
  const effectiveRoles = isAdmin ? [...userRoles, "admin"] : userRoles;
  return Object.fromEntries(
    Object.entries(CLOUD_WIDGETS).filter(([, def]) => {
      if (def.requiredRoles) {
        return def.requiredRoles.some((r) => effectiveRoles.includes(r));
      }
      if (def.adminOnly) return isAdmin;
      return true;
    })
  );
}
